// CRM link sessions (MS-6620, T4.6).
//
// A salesperson arrives from a CRM — an OutPerform signed link, a legacy Salesforce URL, an MHub
// CRM opportunity. book-api verifies that link on its own server, stores what it carried, and
// sends the browser here with nothing but an opaque session id:
//
//   GET /op/link/?<signed query>   ->  303  /crm/continue/<id>
//
// The id is all the SPA ever sees. The buyer's name, phone and email are fetched from
// `GET /crm/links/<id>/` after login, so they are never in a URL, browser history, referrer or
// analytics event. Everything in this module exists to keep that true.
//
// This file holds the decisions and no rendering, because that is what this repository's tests can
// reach: jest here transforms plain JS, not JSX.

export const CRM_LINK_PARAM = 'crm_link';

// One key per tab. sessionStorage, not localStorage or a cookie: a second tab must not inherit
// somebody else's booking session, and nothing about this should outlive the tab.
const STORAGE_KEY = 'mhub.book.crmLink';

// linkIDFromRoute reads the session id from the route.
//
// The path segment is the real contract — book-api redirects to `/crm/continue/<id>` — and the
// query is accepted as well because the deep-link convention was drafted with
// `?crm_link=<id>` and a link already in somebody's hands may still use it. The path wins when
// both are present.
export function linkIDFromRoute(params, location) {
  const fromPath = params && params.id;
  if (typeof fromPath === 'string' && fromPath.trim()) return fromPath.trim();
  const query = (location && location.query) || {};
  const fromQuery = query[CRM_LINK_PARAM];
  if (typeof fromQuery === 'string' && fromQuery.trim()) return fromQuery.trim();
  return '';
}

function safeSessionStorage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (e) {
    // Accessing the property itself throws when site data is blocked.
    return null;
  }
}

// rememberLinkID mirrors the id into the tab so a refresh, a back, or the trip through login does
// not lose it. Storage can throw — a private window, a browser set to block site data — and a
// booking must not fail because of that, so every access is guarded and the id in the URL remains
// the source of truth.
export function rememberLinkID(id, storage) {
  const store = storage || safeSessionStorage();
  if (!store || !id) return false;
  try {
    store.setItem(STORAGE_KEY, id);
    return true;
  } catch (e) {
    return false;
  }
}

export function recallLinkID(storage) {
  const store = storage || safeSessionStorage();
  if (!store) return '';
  try {
    return store.getItem(STORAGE_KEY) || '';
  } catch (e) {
    return '';
  }
}

export function forgetLinkID(storage) {
  const store = storage || safeSessionStorage();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch (e) {
    // nothing to do: the id is short-lived and the session is bound server-side anyway
  }
}

// newRequestGuard stamps each read so only the newest one may act.
//
// Reading a session is a network round trip, and in that time the salesperson can navigate away or
// a second link can arrive in the same tab. An abandoned read that still redirects would take them
// somewhere they never asked to go, on a session nobody is opening any more — and a late rejection
// would clear the mirror the NEW link had just written. Both are silent, which is why the rule is
// a named thing with tests rather than a comparison buried in a callback.
export function newRequestGuard() {
  let current = 0;
  return {
    // begin marks everything already in flight as obsolete and returns this read's token.
    begin() {
      current += 1;
      return current;
    },
    // invalidate abandons whatever is in flight without starting anything: unmounting.
    invalidate() {
      current += 1;
    },
    isCurrent(token) {
      return token === current;
    },
  };
}

// destinationFor is where the salesperson goes once the session is read.
//
// The convention makes `unit` optional: a link may name the unit, or only the project, or neither.
// Each case has a different landing place, and the session id travels with them so the booking
// form can pick the pre-fill up and mark the session consumed on submit.
//
//   unit + project  ->  the booking form for that unit
//   project only    ->  that project's unit list, to choose one
//   neither         ->  the project list
export function destinationFor(session, id) {
  const unit = (session && session.prefill && session.prefill.unit) || {};
  const projectID = unit.project_xid || '';
  const unitID = unit.unit_xid || '';
  const carry = id ? `${CRM_LINK_PARAM}=${encodeURIComponent(id)}` : '';
  const withCarry = (path, sep) => (carry ? `${path}${sep}${carry}` : path);

  if (projectID && unitID) {
    return withCarry(
      `/bookings/new/?projectID=${encodeURIComponent(projectID)}&unitID=${encodeURIComponent(unitID)}`,
      '&',
    );
  }
  if (projectID) {
    return withCarry(`/projects/${encodeURIComponent(projectID)}/units/`, '?');
  }
  return withCarry('/projects/', '?');
}

// withLinkID carries the session id from the page a salesperson is on to the page they are going
// to. The unit list a project-only link landed on (destinationFor) must hand the id to the unit
// page, and the unit page to the booking form, or the form opens with no session: no pre-fill, no
// consumption on submit, and a refusal from the walk-in gate for internal staff (MS-6631).
//
// Only the route is read — the tab's mirror is deliberately not — so a page reached without the id
// hands on nothing, and a stale session cannot re-attach itself to an unrelated booking. `path`
// may already carry a query.
export function withLinkID(path, location) {
  const id = linkIDFromRoute({}, location);
  if (!id) return path;
  const sep = path.indexOf('?') >= 0 ? '&' : '?';
  return `${path}${sep}${CRM_LINK_PARAM}=${encodeURIComponent(id)}`;
}

// One live booking per lead (MS-6671, T4.11).
//
// OutPerform holds one booking per lead, so a booking-creation link for a lead whose MHub booking
// is still live opens THAT booking instead of a new form. book-api decides this in two places and
// tells the SPA in two ways: at entry, `GET /op/link/` answers 303 straight to
// `/bookings/<xid>/?crm_entry=existing_booking`; at submit, `POST /bookings/` answers 409 with
// `X-CRM-Link-Reason: existing_booking` and the booking in `X-CRM-Booking-XID`, and the form
// navigates to the same place. The marker in the query is a constant word so the overview can say
// why the salesperson landed there; nothing from the link is ever in it.
export const ENTRY_PARAM = 'crm_entry';
export const ENTRY_EXISTING_BOOKING = 'existing_booking';
export const EXISTING_BOOKING_REASON = 'existing_booking';
export const EXISTING_BOOKING_HEADER = 'X-CRM-Booking-XID';

// existingBookingPath is where the form sends the salesperson when submit answers 409: the same
// URL book-api's entry route redirects to, so the overview treats both arrivals alike.
export function existingBookingPath(bookingID) {
  const id = String(bookingID || '').trim();
  if (!id) return '';
  return `/bookings/${encodeURIComponent(id)}/?${ENTRY_PARAM}=${ENTRY_EXISTING_BOOKING}`;
}

// arrivedAtExistingBooking reads the marker off the overview's route. Only the exact word counts;
// a repeated parameter (an array) or anything else is not an arrival.
export function arrivedAtExistingBooking(query) {
  const q = query || {};
  return q[ENTRY_PARAM] === ENTRY_EXISTING_BOOKING;
}

// existingBookingFromResponse reads the 409 the booking create answers for a guarded lead: the
// reason header must say so AND the booking header must name a booking; either alone is not an
// instruction to navigate. Returns the booking id, or '' when this is some other failure.
export function existingBookingFromResponse(res) {
  const header = (name) => {
    try {
      return (res && res.headers && res.headers.get(name)) || '';
    } catch (e) {
      return '';
    }
  };
  if (header('X-CRM-Link-Reason') !== EXISTING_BOOKING_REASON) return '';
  return header(EXISTING_BOOKING_HEADER).trim();
}

// Machine reasons book-api's link handlers return (handlers/crm_links.go). They are about the
// SESSION — whose it is, whether it is still open — not about the signed link, which was judged
// before the browser ever got here. Telling these apart is safe and useful: the person reading the
// screen is logged in, and each one has a different thing to do next.
//
// Reading a session (GET /crm/links/<id>/) reaches the first five. The last three come from the
// same shared mapping (writeLinkError) but through CREATING one — POST /crm/links/, which MS-6621
// uses for the legacy Salesforce arrival — so they cannot arrive here today. They are listed
// because one reader serves both calls, which is better than two that drift apart.
export const LINK_ERROR_REASONS = [
  // reachable when reading a session
  'not_found',
  'expired',
  'consumed',
  'other_user',
  'unavailable',
  // reachable when creating one (POST /crm/links/, MS-6621), not when reading
  'provider_disabled',
  'unknown_provider',
  'too_many_parameters',
];

// rejectionFor turns a failed read into what the page needs: a copy key, and whether trying again
// could possibly help. An unknown reason is treated as unavailable rather than guessed at.
export function rejectionFor(reason, reference) {
  const known = LINK_ERROR_REASONS.indexOf(reason) >= 0 ? reason : 'unavailable';
  return {
    reason: known,
    reference: reference || '',
    key: `crmLink.${known}`,
    // Only a transport or server problem is worth a second attempt. A consumed session or one
    // belonging to somebody else will answer the same way for ever, and a retry button that
    // cannot work is worse than none.
    retryable: known === 'unavailable',
  };
}

// rejectionFromResponse reads a failed session read.
//
// Where the reason lives is worth being exact about, because getting it wrong is silent. Reading a
// session (`GET /crm/links/<id>/`) answers through book-api's `writeLinkError`, which puts the
// machine reason in the JSON BODY — `{error, reason, detail, provider, reference}` — and sets only
// `X-CRM-Link-Reference` as a header. The `X-CRM-Link-Reason` header belongs to a different
// handler (`writeLinkErrorText`, the booking-create path, MS-6621). Trusting the header here would
// silently fall through to the status for every rejection, and a consumed session would read as
// expired because both answer 410.
//
// So: the body first, then the header for the endpoints that do send it, then the status.
export function rejectionFromResponse(res, body) {
  const header = (name) => {
    try {
      return (res && res.headers && res.headers.get(name)) || '';
    } catch (e) {
      return '';
    }
  };
  const reference = (body && body.reference) || header('X-CRM-Link-Reference');

  if (body && body.reason) return rejectionFor(body.reason, reference);
  const headerReason = header('X-CRM-Link-Reason');
  if (headerReason) return rejectionFor(headerReason, reference);

  // No body and no header: a proxy stripped them, or something answered that never reached the
  // handler. The status is the last thing left, and a status can stand for more than one reason —
  // so guess only where the candidates lead to the SAME advice.
  //
  //   410  expired or consumed  -> both mean "open it again from the CRM". Guessing is harmless.
  //   403  other_user or provider_disabled -> "go and ask your colleague" versus "contact
  //        support". Opposite advice, so guessing would send somebody to bother a colleague over
  //        a switch that is off. Say we could not read it instead.
  //
  // Not knowing here is usually transient — an unreadable body means a proxy or an error page got
  // in the way — which is why the honest answer is also the one worth trying again.
  const status = (res && res.status) || 0;
  if (status === 404) return rejectionFor('not_found', reference);
  if (status === 410) return rejectionFor('expired', reference);
  return rejectionFor('unavailable', reference);
}



// WEBPACK FOOTER //
// ./src/utils/crmLink.js